import { FormControl, FormControlLabel, RadioGroup } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import FormValidator from '../../../core/FormValidator';
import { ResourceTextApplication } from '../../../core/resources';
import { IAnimalAnnouncementAnimal } from '../../../interfaces/IAnnouncement';
import { IMedicatedAnimals } from './ChainInformationDialog'; // or move type to shared file
import { ATextInput, ADateInput, ANumberInput } from '../../../common/inputs';
import { ARadiobutton } from '../../../common/buttons/ARadioButton';
import { ACheckbox } from '../../../common/buttons/ACheckbox';
import { AAddButton, ADeleteButton } from '../../../common/buttons';
import { Checkbox } from '@mui/material';
import { styled } from '@mui/system';
import { useEffect } from 'react';

export type ChainInfoMedicationProps = {
    animals: IAnimalAnnouncementAnimal[];
    readOnly: boolean;

    medicatedFeed: boolean | null;
    onMedicatedFeedChange: (v: boolean) => void;

    medicalAccounting: boolean | null;
    onMedicalAccountingChange: (v: boolean) => void;

    medicationInfo: string;
    onMedicationInfoTextChange: (v: string) => void;

    waitingTimeOver: boolean | null;
    onWaitingTimeOverChange: (v: boolean) => void;

    medicatedAnimals: IMedicatedAnimals[];
    onToggleAnimalMedication: (animalId: string, euIdentifier: string) => void;

    medicationInfoList: IMedicationInfo[];
    onMedicationInfoChange: (
        animalId: string,
        medicationIndex: number,
        field: keyof IMedicationEntry,
        value: string,
    ) => void;
    onAddMedication: (animalId: string, euIdentifier: string) => void;
    onRemoveMedication: (animalId: string, medicationIndex: number) => void;

    validator: FormValidator;
    medicationNumberFieldValidator: FormValidator;
};

export const WhiteCheckbox = styled(Checkbox)(({ theme }) => ({
    color: '#ffffff',
    '&.Mui-checked': {
        color: '#ffffff',
    },
}));

const StyledFormControlLabel = styled(FormControlLabel)({
    '&.Mui-disabled .MuiFormControlLabel-label': {
        color: '#ffffff',
    },
});

export interface IMedicationEntry {
    medicationName: string;
    medicationLength: string;
    medicationEndDate: string;
    medicationWaitingTime: string;
}

export interface IMedicationInfo {
    animalId: string;
    euIdentifier: string;
    medications: IMedicationEntry[];
}

export default function ChainInfoMedication({
    animals,
    readOnly,
    medicatedFeed,
    onMedicatedFeedChange,
    medicalAccounting,
    onMedicalAccountingChange,
    medicationInfo,
    onMedicationInfoTextChange,
    waitingTimeOver,
    onWaitingTimeOverChange,
    medicatedAnimals,
    onToggleAnimalMedication,
    medicationInfoList,
    onMedicationInfoChange,
    onAddMedication,
    onRemoveMedication,
    validator,
    medicationNumberFieldValidator,
}: ChainInfoMedicationProps) {
    const { t } = useTranslation<ResourceTextApplication[]>(['AnelmaChainInformation']);

    const checkAnimalMedicated = (animalId: string) => {
        const isMarkedMedicated =
            medicatedAnimals.find((a) => a.animalId === animalId)?.isMedicated ?? false;

        // Also check if animal has medication details in medicationInfoList (even with empty medications array)
        const hasMedicationInfo = medicationInfoList.some((info) => info.animalId === animalId);

        // Animal is medicated if either condition is true
        return isMarkedMedicated || hasMedicationInfo;
    };

    const getMedicationInfo = (animalId: string): IMedicationInfo => {
        return (
            medicationInfoList.find((info) => info.animalId === animalId) || {
                animalId,
                euIdentifier: '',
                medications: [],
            }
        );
    };

    const shouldShowMedicationFields = (animalId: string): boolean => {
        const info = getMedicationInfo(animalId);
        const hasAnyValue = info.medications.some(
            (med) =>
                med.medicationName ||
                med.medicationLength ||
                med.medicationEndDate ||
                med.medicationWaitingTime,
        );

        // Show fields if any field has data (loaded data with values)
        // OR if form is editable (new/editing form - let user fill the fields)
        // Hide only for readOnly old data with no field values
        return hasAnyValue || !readOnly;
    };

    const canAddMoreMedications = (animalId: string): boolean => {
        const info = getMedicationInfo(animalId);
        return info.medications.length < 4;
    };

    return (
        <>
            <Grid>
                <FormControl>
                    <h5>{t('AnelmaChainInformation:1012')}</h5>
                    <RadioGroup>
                        <FormControlLabel
                            disabled={readOnly}
                            checked={medicatedFeed === false}
                            onChange={() => onMedicatedFeedChange(false)}
                            control={<ARadiobutton />}
                            label={t('AnelmaChainInformation:1013')}
                            labelPlacement='end'
                        />
                        <FormControlLabel
                            disabled={readOnly}
                            checked={medicatedFeed === true}
                            onChange={() => onMedicatedFeedChange(true)}
                            control={<ARadiobutton />}
                            label={t('AnelmaChainInformation:1014')}
                            labelPlacement='end'
                        />
                    </RadioGroup>
                </FormControl>
            </Grid>

            {medicatedFeed === true && (
                <Grid container size={12}>
                    <Grid>
                        <FormControlLabel
                            disabled={readOnly}
                            checked={medicalAccounting === true}
                            onChange={() => onMedicalAccountingChange(!medicalAccounting)}
                            control={<ACheckbox />}
                            label={t('AnelmaChainInformation:1015')}
                            labelPlacement='end'
                        />

                        {medicationInfo.length > 0 && (
                            <Grid style={{ width: '100%', marginTop: '16px' }}>
                                <ATextInput
                                    disabled={readOnly}
                                    id='medication-info'
                                    label={t('AnelmaChainInformation:1016')}
                                    onChange={(v) => onMedicationInfoTextChange(v)}
                                    required={!medicalAccounting || false}
                                    validator={validator}
                                    value={medicationInfo}
                                    lengthBoundaryName='AnelmaChainInformation:TextFieldLength'
                                />
                            </Grid>
                        )}
                        <Grid>
                            <FormControlLabel
                                disabled={readOnly}
                                checked={waitingTimeOver === true}
                                onChange={() => onWaitingTimeOverChange(!waitingTimeOver)}
                                control={<ACheckbox />}
                                label={t('AnelmaChainInformation:1017')}
                                labelPlacement='end'
                            />
                        </Grid>
                    </Grid>
                </Grid>
            )}

            <Grid container>
                <Grid>
                    <h5>{t('AnelmaChainInformation:1009')}</h5>
                </Grid>

                {animals.map((item) => (
                    <Grid size={{ sm: 12 }} key={item.AnimalId}>
                        <StyledFormControlLabel
                            style={{
                                color: 'white',
                                backgroundColor: checkAnimalMedicated(item.AnimalId)
                                    ? '#0a4c31'
                                    : '#6d6d6d',
                                width: '100%',
                                display: 'flex',
                                flexFlow: 'row',
                                marginTop: '5px',
                            }}
                            disabled={readOnly || medicatedFeed !== true}
                            checked={checkAnimalMedicated(item.AnimalId)}
                            onChange={() =>
                                onToggleAnimalMedication(item.AnimalId, item.EuIdentifier)
                            }
                            control={<WhiteCheckbox />}
                            label={item.EuIdentifier}
                            labelPlacement='start'
                        />
                        {checkAnimalMedicated(item.AnimalId) &&
                            shouldShowMedicationFields(item.AnimalId) && (
                                <Grid container spacing={2} style={{ marginTop: '8px' }}>
                                    {getMedicationInfo(item.AnimalId).medications.map(
                                        (medication, index) => (
                                            <Grid
                                                container
                                                spacing={2}
                                                key={index}
                                                style={{
                                                    marginBottom: '16px',
                                                    paddingLeft: '16px',
                                                }}
                                            >
                                                <Grid size={{ xs: 12 }}>
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        <strong>
                                                            {t(
                                                                'AnelmaChainInformation:1068',
                                                            ).replace(
                                                                '{0}',
                                                                (index + 1).toString(),
                                                            )}
                                                        </strong>
                                                        {!readOnly &&
                                                            getMedicationInfo(item.AnimalId)
                                                                .medications.length > 1 && (
                                                                <ADeleteButton
                                                                    onClick={() => {
                                                                        onRemoveMedication(
                                                                            item.AnimalId,
                                                                            index,
                                                                        );
                                                                    }}
                                                                    children={t(
                                                                        'AnelmaChainInformation:1067',
                                                                    )}
                                                                ></ADeleteButton>
                                                            )}
                                                    </div>
                                                </Grid>
                                                <Grid size={{ sm: 6 }}>
                                                    <ATextInput
                                                        disabled={readOnly}
                                                        id={`medication-name-${item.AnimalId}-${index}`}
                                                        label={t('AnelmaChainInformation:1062')}
                                                        onChange={(v) =>
                                                            onMedicationInfoChange(
                                                                item.AnimalId,
                                                                index,
                                                                'medicationName',
                                                                v,
                                                            )
                                                        }
                                                        required={shouldShowMedicationFields(
                                                            item.AnimalId,
                                                        )}
                                                        validator={validator}
                                                        value={medication.medicationName}
                                                        lengthBoundaryName='AnelmaChainInformation:TextFieldLength'
                                                    />

                                                    <ATextInput
                                                        disabled={readOnly}
                                                        id={`medication-length-${item.AnimalId}-${index}`}
                                                        label={t('AnelmaChainInformation:1063')}
                                                        onChange={(v) =>
                                                            onMedicationInfoChange(
                                                                item.AnimalId,
                                                                index,
                                                                'medicationLength',
                                                                v.replace(/ /g, ''),
                                                            )
                                                        }
                                                        required={shouldShowMedicationFields(
                                                            item.AnimalId,
                                                        )}
                                                        validator={medicationNumberFieldValidator}
                                                        regExp={/^-?\d*\s*$/}
                                                        value={medication.medicationLength}
                                                        lengthBoundaryName='AnelmaChainInformation:TextFieldLength'
                                                    />
                                                </Grid>
                                                <Grid size={{ sm: 6 }}>
                                                    <ADateInput
                                                        disabled={readOnly}
                                                        id={`medication-end-date-${item.AnimalId}-${index}`}
                                                        label={t('AnelmaChainInformation:1064')}
                                                        onChange={(d) => {
                                                            onMedicationInfoChange(
                                                                item.AnimalId,
                                                                index,
                                                                'medicationEndDate',
                                                                d && moment(d).isValid()
                                                                    ? moment(d).format('YYYY-MM-DD')
                                                                    : '',
                                                            );
                                                        }
                                                        }
                                                        required
                                                        validator={validator}
                                                        value={
                                                            medication.medicationEndDate
                                                                ? moment(
                                                                      medication.medicationEndDate,
                                                                  ).toDate()
                                                                : null
                                                        }
                                                        // custom validation rule, otherwise "Invalid date" is a valid date
                                                        error={medication.medicationEndDate === ''}
                                                        // 01.01.1900 is min for the component, Jan is 0
                                                        minDate={new Date(1900, 0, 1)}
                                                    />

                                                    <ATextInput
                                                        disabled={readOnly}
                                                        id={`medication-waiting-time-${item.AnimalId}-${index}`}
                                                        label={t('AnelmaChainInformation:1065')}
                                                        onChange={(v) => {
                                                            onMedicationInfoChange(
                                                                item.AnimalId,
                                                                index,
                                                                'medicationWaitingTime',
                                                                v.replace(/ /g, ''),
                                                            )
                                                        }}
                                                        required={shouldShowMedicationFields(
                                                            item.AnimalId,
                                                        )}
                                                        validator={medicationNumberFieldValidator}
                                                        regExp={/^-?\d*\s*$/}
                                                        value={medication.medicationWaitingTime}
                                                    />
                                                </Grid>
                                            </Grid>
                                        ),
                                    )}
                                    {!readOnly && canAddMoreMedications(item.AnimalId) && (
                                        <Grid size={{ xs: 12 }}>
                                            <AAddButton
                                                onClick={() =>
                                                    onAddMedication(
                                                        item.AnimalId,
                                                        item.EuIdentifier,
                                                    )
                                                }
                                            >
                                                {t('AnelmaChainInformation:1066')}
                                            </AAddButton>
                                        </Grid>
                                    )}
                                </Grid>
                            )}
                    </Grid>
                ))}
            </Grid>
        </>
    );
}
